import { defineHotpadsEvent, trackHotpadsEventDefinition } from '../track';
import type { ListingArg, RentalSubmitArg, TriggerArg } from './types';

const LEGACY_SOURCE = 'srcLegacy/shared/models/Clickstream/ContactClickstreamEvents.ts';

const INLINE_CONTACT_FORM_VIEW = defineHotpadsEvent({
  key: 'contact.inline_form_view',
  eventTypeId: '5568',
  eventTypeVersionId: '1',
  eventTemplateId: '4',
  eventTemplateVersionId: '1',
  legacySource: LEGACY_SOURCE,
  legacyExport: 'emitTrackInlineContactFormViewEvent',
  trigger: {
    type: 'impression',
    location: 'home_details',
    source: 'hdp_contact_form',
    object: 'home_details_component|inline_form',
  },
  semanticEvent: 'view_content',
});

const MODAL_CONTACT_FORM_VIEW = defineHotpadsEvent({
  key: 'contact.modal_form_view',
  eventTypeId: '5569',
  eventTypeVersionId: '2',
  eventTemplateId: '4',
  eventTemplateVersionId: '1',
  legacySource: LEGACY_SOURCE,
  legacyExport: 'emitTrackModalContactFormViewEvent',
  trigger: {
    type: 'impression',
    location: 'home_details',
    source: 'modal_contact_form',
    object: 'home_details_component|modal_contact_form',
  },
  semanticEvent: 'view_content',
});

const INLINE_CONTACT_SUBMIT = defineHotpadsEvent({
  key: 'contact.inline_submit',
  eventTypeId: '5570',
  eventTypeVersionId: '2',
  eventTemplateId: '68',
  eventTemplateVersionId: '1',
  legacySource: LEGACY_SOURCE,
  legacyExport: 'emitTrackInlineContactSubmitEvent',
  trigger: {
    type: 'interaction',
    location: 'home_details',
    source: 'button_to_complete_contact_form',
    object: 'home_details_component|inline_form',
  },
  semanticEvent: 'inline_contact_submit',
});

const MODAL_CONTACT_SUBMIT = defineHotpadsEvent({
  key: 'contact.modal_submit',
  eventTypeId: '5571',
  eventTypeVersionId: '5',
  eventTemplateId: '68',
  eventTemplateVersionId: '1',
  legacySource: LEGACY_SOURCE,
  legacyExport: 'emitTrackModalContactSubmitEvent',
  trigger: {
    type: 'interaction',
    location: 'home_details',
    source: 'button_to_complete_contact_form',
    object: 'home_details_component|modal_contact_form',
  },
  semanticEvent: 'modal_contact_submit',
});

const SIMILAR_LISTING_MESSAGE_CLICK = defineHotpadsEvent({
  key: 'contact.similar_listing_message_click',
  eventTypeId: '7276',
  eventTypeVersionId: '3',
  eventTemplateId: '4',
  eventTemplateVersionId: '1',
  legacySource: LEGACY_SOURCE,
  legacyExport: 'emitTrackSimilarListingMultiContactMessageButtonClickEvent',
  trigger: {
    type: 'interaction',
    location: 'home_details',
    source: 'button_to_send_message',
    object: 'home_details|post_contact_modal',
  },
  semanticEvent: 'send_message_similar_listing',
});

const SIMILAR_LISTING_CONTACT_SUBMIT = defineHotpadsEvent({
  key: 'contact.similar_listing_submit',
  eventTypeId: '7283',
  eventTypeVersionId: '2',
  eventTemplateId: '68',
  eventTemplateVersionId: '1',
  legacySource: LEGACY_SOURCE,
  legacyExport: 'emitTrackSimilarListingMultiContactSubmitEvent',
  trigger: {
    type: 'interaction',
    location: 'home_details',
    source: 'button_to_send_message',
    object: 'home_details|post_contact_modal',
  },
  semanticEvent: 'similar_listing_contact_submit',
});

const REQUEST_CONTACT_START = defineHotpadsEvent({
  key: 'contact.request_start',
  eventTypeId: '5574',
  eventTypeVersionId: '1',
  eventTemplateId: '4',
  eventTemplateVersionId: '1',
  legacySource: LEGACY_SOURCE,
  legacyExport: 'emitTrackRequestContactStartEvent',
  trigger: {
    type: 'interaction',
    location: 'home_details',
    source: 'button_to_open_contact_form',
    object: 'home_details_component|summary',
  },
  semanticEvent: 'request_contact_start',
});

const FREE_LISTING_LEAD = defineHotpadsEvent({
  key: 'contact.email_lead_free',
  eventTypeId: '5575',
  eventTypeVersionId: '6',
  eventTemplateId: '4',
  eventTemplateVersionId: '1',
  legacySource: LEGACY_SOURCE,
  legacyExport: 'emitTrackFreeListingLeadEvent',
  trigger: {
    type: 'interaction',
    location: 'home_details',
    source: 'button_to_complete_contact_form',
    object: 'home_details_component|modal_contact_form',
  },
  semanticEvent: 'lead_email_free',
});

const PAID_LISTING_LEAD = defineHotpadsEvent({
  key: 'contact.email_lead_paid',
  eventTypeId: '5576',
  eventTypeVersionId: '6',
  eventTemplateId: '4',
  eventTemplateVersionId: '1',
  legacySource: LEGACY_SOURCE,
  legacyExport: 'emitTrackPaidListingLeadEvent',
  trigger: {
    type: 'interaction',
    location: 'home_details',
    source: 'button_to_complete_contact_form',
    object: 'home_details_component|modal_contact_form',
  },
  semanticEvent: 'lead_email_paid',
});

const DEMOTED_LISTING_LEAD = defineHotpadsEvent({
  key: 'contact.email_lead_paid_demoted',
  eventTypeId: '5577',
  eventTypeVersionId: '6',
  eventTemplateId: '4',
  eventTemplateVersionId: '1',
  legacySource: LEGACY_SOURCE,
  legacyExport: 'emitTrackDemotedListingLeadEvent',
  trigger: {
    type: 'interaction',
    location: 'home_details',
    source: 'button_to_complete_contact_form',
    object: 'home_details_component|modal_contact_form',
  },
  semanticEvent: 'lead_email_paid_demoted',
});

const PHONE_LEAD_FREE = defineHotpadsEvent({
  key: 'contact.phone_lead_free',
  eventTypeId: '5597',
  eventTypeVersionId: '3',
  eventTemplateId: '4',
  eventTemplateVersionId: '1',
  legacySource: LEGACY_SOURCE,
  legacyExport: 'emitTrackPhoneLeadForFreePropertyEvent',
  trigger: {
    type: 'interaction',
    location: 'home_details',
    source: 'link_to_phone_number',
    object: 'home_details_component|modal_contact_form',
  },
  semanticEvent: 'lead_phone_free',
});

const PHONE_LEAD_DEMOTED = defineHotpadsEvent({
  key: 'contact.phone_lead_paid_demoted',
  eventTypeId: '5599',
  eventTypeVersionId: '3',
  eventTemplateId: '4',
  eventTemplateVersionId: '1',
  legacySource: LEGACY_SOURCE,
  legacyExport: 'emitTrackPhoneLeadForDemotedPropertyEvent',
  trigger: {
    type: 'interaction',
    location: 'home_details',
    source: 'link_to_phone_number',
    object: 'home_details_component|modal_contact_form',
  },
  semanticEvent: 'lead_phone_paid_demoted',
});

const PHONE_LEAD_PAID = defineHotpadsEvent({
  key: 'contact.phone_lead_paid',
  eventTypeId: '5600',
  eventTypeVersionId: '4',
  eventTemplateId: '4',
  eventTemplateVersionId: '1',
  legacySource: LEGACY_SOURCE,
  legacyExport: 'emitTrackPhoneLeadForPaidPropertyEvent',
  trigger: {
    type: 'interaction',
    location: 'home_details',
    source: 'link_to_phone_number',
    object: 'home_details_component|modal_contact_form',
  },
  semanticEvent: 'lead_phone_paid',
});

export function emitTrackInlineContactFormViewEvent({ listing }: ListingArg) {
  trackHotpadsEventDefinition(INLINE_CONTACT_FORM_VIEW, { listing });
}

export function emitTrackModalContactFormViewEvent({
  listing,
  triggerLocation,
  triggerObject,
}: ListingArg & TriggerArg) {
  trackHotpadsEventDefinition(MODAL_CONTACT_FORM_VIEW, { listing, triggerLocation, triggerObject });
}

export function emitTrackInlineContactSubmitEvent({
  listing,
  rentalSubmitId,
  triggerLocation,
}: ListingArg & RentalSubmitArg & TriggerArg) {
  trackHotpadsEventDefinition(INLINE_CONTACT_SUBMIT, { listing, rentalSubmitId, triggerLocation });
}

export function emitTrackModalContactSubmitEvent({
  listing,
  rentalSubmitId,
  triggerLocation,
  triggerObject,
  triggerSource,
}: ListingArg & RentalSubmitArg & TriggerArg) {
  trackHotpadsEventDefinition(MODAL_CONTACT_SUBMIT, {
    listing,
    rentalSubmitId,
    triggerLocation,
    triggerObject,
    triggerSource,
  });
}

export function emitTrackSimilarListingMultiContactMessageButtonClickEvent({
  listing,
  triggerLocation,
  triggerObject,
}: ListingArg & TriggerArg) {
  trackHotpadsEventDefinition(SIMILAR_LISTING_MESSAGE_CLICK, { listing, triggerLocation, triggerObject });
}

export function emitTrackSimilarListingMultiContactSubmitEvent({
  listing,
  rentalSubmitId,
  triggerLocation,
  triggerObject,
}: ListingArg & RentalSubmitArg & TriggerArg) {
  trackHotpadsEventDefinition(SIMILAR_LISTING_CONTACT_SUBMIT, {
    listing,
    rentalSubmitId,
    triggerLocation,
    triggerObject,
  });
}

export function emitTrackRequestContactStartEvent({
  listing,
  triggerLocation,
  triggerObject,
}: ListingArg & TriggerArg) {
  trackHotpadsEventDefinition(REQUEST_CONTACT_START, { listing, triggerLocation, triggerObject });
}

export function emitTrackFreeListingLeadEvent({
  listing,
  triggerLocation,
  triggerObject,
  triggerSource,
}: ListingArg & TriggerArg) {
  trackHotpadsEventDefinition(FREE_LISTING_LEAD, { listing, triggerLocation, triggerObject, triggerSource });
}

export function emitTrackPaidListingLeadEvent({
  listing,
  triggerLocation,
  triggerObject,
  triggerSource,
}: ListingArg & TriggerArg) {
  trackHotpadsEventDefinition(PAID_LISTING_LEAD, { listing, triggerLocation, triggerObject, triggerSource });
}

export function emitTrackDemotedListingLeadEvent({
  listing,
  triggerLocation,
  triggerObject,
  triggerSource,
}: ListingArg & TriggerArg) {
  trackHotpadsEventDefinition(DEMOTED_LISTING_LEAD, { listing, triggerLocation, triggerObject, triggerSource });
}

export function emitTrackPhoneLeadForFreePropertyEvent({
  listing,
  triggerLocation,
  triggerObject,
}: ListingArg & TriggerArg) {
  trackHotpadsEventDefinition(PHONE_LEAD_FREE, { listing, triggerLocation, triggerObject });
}

export function emitTrackPhoneLeadForDemotedPropertyEvent({
  listing,
  triggerLocation,
  triggerObject,
}: ListingArg & TriggerArg) {
  trackHotpadsEventDefinition(PHONE_LEAD_DEMOTED, { listing, triggerLocation, triggerObject });
}

export function emitTrackPhoneLeadForPaidPropertyEvent({
  listing,
  triggerLocation,
  triggerObject,
}: ListingArg & TriggerArg) {
  trackHotpadsEventDefinition(PHONE_LEAD_PAID, { listing, triggerLocation, triggerObject });
}
